import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY

export type PushSubscriptionData = {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function usePushNotifications() {
  const { tenantId } = useAuth()
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [loading, setLoading] = useState(false)

  const checkSubscription = async () => {
    if (!tenantId) return

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      setIsSubscribed(!!subscription)
    } catch (err) {
      console.error('[Push] Check subscription error:', err)
    }
  }

  // Check current permission and subscription status
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[Push] Not supported')
      return
    }

    Notification.requestPermission().then(setPermission)
    checkSubscription()
  }, [tenantId])

  const subscribe = useCallback(async () => {
    if (!tenantId || !VAPID_PUBLIC_KEY) {
      toast.error('Configuración de push incompleta')
      return false
    }

    setLoading(true)
    try {
      // Request permission
      const perm = await Notification.requestPermission()
      setPermission(perm)

      if (perm !== 'granted') {
        toast.error('Permiso de notificaciones denegado')
        return false
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      })

      // Send subscription to backend
      const p256dhKey = subscription.getKey('p256dh') as Uint8Array<ArrayBuffer> | null
      const authKey = subscription.getKey('auth') as Uint8Array<ArrayBuffer> | null
      const subData: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...Array.from(p256dhKey!))),
          auth: btoa(String.fromCharCode(...Array.from(authKey!))),
        },
      }

      const { error } = await supabase
        .from('push_subscriptions')
        .upsert(
          {
            tenant_id: tenantId,
            endpoint: subData.endpoint,
            p256dh: subData.keys.p256dh,
            auth: subData.keys.auth,
            user_agent: navigator.userAgent,
          },
          { onConflict: 'tenant_id,endpoint' }
        )

      if (error) throw error

      setIsSubscribed(true)
      toast.success('Notificaciones activadas')
      return true
    } catch (err) {
      console.error('[Push] Subscribe error:', err)
      toast.error('Error al activar notificaciones')
      return false
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  const unsubscribe = useCallback(async () => {
    if (!tenantId) return

    setLoading(true)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        await subscription.unsubscribe()

        await supabase.from('push_subscriptions').delete().eq('endpoint', subscription.endpoint)
      }

      setIsSubscribed(false)
      toast.success('Notificaciones desactivadas')
    } catch (err) {
      console.error('[Push] Unsubscribe error:', err)
      toast.error('Error al desactivar notificaciones')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  const toggle = useCallback(async () => {
    if (isSubscribed) {
      await unsubscribe()
    } else {
      await subscribe()
    }
  }, [isSubscribed, subscribe, unsubscribe])

  return {
    permission,
    isSubscribed,
    loading,
    subscribe,
    unsubscribe,
    toggle,
    isSupported: 'serviceWorker' in navigator && 'PushManager' in window,
  }
}